import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, LogOut, Loader2, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import api, { errorMessage } from "@/lib/api";

const EMPTY_CREATE = {
  name: "", tax_id: "", address: "", contact_email: "", contact_phone: "",
  chief: { name: "", email: "", password: "" },
};

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/companies");
      setCompanies(res.data);
    } catch (e) {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/companies", createForm);
      toast.success("Empresa criada", { description: `${createForm.name} · chefe: ${createForm.chief.email}` });
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      fetchCompanies();
    } catch (e) {
      toast.error("Erro ao criar empresa", { description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (company) => {
    setEditTarget(company);
    setEditForm({
      name: company.name || "",
      tax_id: company.tax_id || "",
      address: company.address || "",
      contact_email: company.contact_email || "",
      contact_phone: company.contact_phone || "",
      active: company.active,
      subscription_active_until: company.subscription_active_until || "",
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editForm, subscription_active_until: editForm.subscription_active_until || null };
      await api.patch(`/companies/${editTarget.id}`, payload);
      toast.success("Empresa atualizada");
      setEditTarget(null);
      fetchCompanies();
    } catch (e) {
      toast.error("Erro ao atualizar empresa", { description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            title="Voltar ao visualizador"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 bg-foreground flex items-center justify-center">
            <Building2 className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none">
            <h1 className="font-head text-sm font-bold tracking-tight">PAINEL SUPER-ADMIN</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">Gestão de empresas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground mono hidden sm:inline">{user?.name}</span>
          <button
            onClick={logout}
            title="Terminar sessão"
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Empresas</h2>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-[4px]" data-testid="new-company-btn">
                <Plus className="w-4 h-4" /> Nova empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nova empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label>Nome da empresa</Label>
                    <Input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>NIF</Label>
                    <Input value={createForm.tax_id} onChange={(e) => setCreateForm({ ...createForm, tax_id: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Telefone</Label>
                    <Input value={createForm.contact_phone} onChange={(e) => setCreateForm({ ...createForm, contact_phone: e.target.value })} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Morada</Label>
                    <Input value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Email de contacto</Label>
                    <Input type="email" value={createForm.contact_email} onChange={(e) => setCreateForm({ ...createForm, contact_email: e.target.value })} />
                  </div>
                </div>

                <Separator />
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Responsável (chefe da empresa)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label>Nome</Label>
                    <Input required value={createForm.chief.name}
                      onChange={(e) => setCreateForm({ ...createForm, chief: { ...createForm.chief, name: e.target.value } })} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Email (login)</Label>
                    <Input required type="email" value={createForm.chief.email}
                      onChange={(e) => setCreateForm({ ...createForm, chief: { ...createForm.chief, email: e.target.value } })} />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Password</Label>
                    <Input required type="password" minLength={8} value={createForm.chief.password}
                      onChange={(e) => setCreateForm({ ...createForm, chief: { ...createForm.chief, password: e.target.value } })} />
                  </div>
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-[4px]">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Criar empresa
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>NIF</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Subscrição até</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline" /> A carregar...
                </TableCell></TableRow>
              ) : companies.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhuma empresa criada.</TableCell></TableRow>
              ) : companies.map((c) => (
                <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="mono text-xs">{c.tax_id || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.contact_email || c.contact_phone || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={c.active ? "default" : "secondary"}>{c.active ? "Ativa" : "Inativa"}</Badge>
                  </TableCell>
                  <TableCell className="mono text-xs">{c.subscription_active_until || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar {editTarget?.name}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleEditSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label>Nome da empresa</Label>
                  <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>NIF</Label>
                  <Input value={editForm.tax_id} onChange={(e) => setEditForm({ ...editForm, tax_id: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefone</Label>
                  <Input value={editForm.contact_phone} onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Morada</Label>
                  <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Email de contacto</Label>
                  <Input type="email" value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} />
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <Label htmlFor="company-active">Empresa ativa</Label>
                <Switch id="company-active" checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} />
              </div>
              <div className="space-y-1.5">
                <Label>Subscrição ativa até</Label>
                <Input type="date" value={editForm.subscription_active_until}
                  onChange={(e) => setEditForm({ ...editForm, subscription_active_until: e.target.value })} />
                <p className="text-[11px] text-muted-foreground">Apenas informativo nesta fase — sem bloqueio automático.</p>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={saving} className="rounded-[4px]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
